// ============================================================================
// Header-only authenticated shell reads
// ============================================================================
// This hook is intentionally used by src/shared/Header.tsx alone. It moves the
// Header's two render reads out of that globally-mounted component while
// retaining its exact refresh signals and its independent failure posture.
// It does not own Header overlay state or any mark-read mutation.
// ============================================================================

import React from 'react';
import {useQuery} from '@tanstack/react-query';

import {todayCentralISO} from '../../lib/dateUtils.js';
import {NOTIFICATIONS_CHANGE_EVENT} from '../../lib/notificationsApi.js';
import {TASK_CHANGE_EVENT} from '../../lib/tasksCenterMutationsApi.js';
import {sb} from '../../lib/supabase.js';
import type {SupabaseClient} from '../../lib/supabaseTypes.js';
import {HEADER_SHELL_RESOURCES, headerShellQueryOptions, headerShellScopeOrNull} from './headerShellResources.js';

export type HeaderShellResourcesInput = {
  authState: unknown;
  rolePreview?: unknown;
  /** AuthContext's boot-owned session readiness/cache epoch. */
  sessionEpoch: number;
  view: string;
  client?: SupabaseClient;
};

export type HeaderShellResourcesResult = {
  taskBadgeCount: number;
  notificationUnread: number;
  notificationRecent: unknown[];
  notificationLoading: boolean;
  notificationFailed: boolean;
  retryNotifications: () => Promise<unknown>;
};

function warnDetail(error: unknown): unknown {
  if (error && typeof error === 'object' && 'message' in error && error.message) return error.message;
  return error;
}

/**
 * Query and refresh the two Header-owned resources. A task failure becomes a
 * zero badge without touching notification state; a notification failure
 * becomes the existing zero/empty/error-panel state without touching tasks.
 */
export function useHeaderShellResources({
  authState,
  rolePreview = '',
  sessionEpoch,
  view,
  client = sb,
}: HeaderShellResourcesInput): HeaderShellResourcesResult {
  const shellScope = React.useMemo(
    () => headerShellScopeOrNull(authState, rolePreview, sessionEpoch),
    [authState, rolePreview, sessionEpoch],
  );
  const subjectId = shellScope?.scope.subjectId || '';
  const today = todayCentralISO();

  const taskOptions = React.useMemo(
    () =>
      headerShellQueryOptions({
        resource: HEADER_SHELL_RESOURCES.TASK_BADGE,
        scope: shellScope,
        params: {subjectId, today},
        client,
      }),
    [client, shellScope, subjectId, today],
  );
  const notificationOptions = React.useMemo(
    () =>
      headerShellQueryOptions({
        resource: HEADER_SHELL_RESOURCES.NOTIFICATIONS,
        scope: shellScope,
        params: {subjectId, limit: 20},
        client,
      }),
    [client, shellScope, subjectId],
  );

  const taskQuery = useQuery(taskOptions);
  const notificationQuery = useQuery(notificationOptions);
  const {data: taskData, error: taskError, refetch: refetchTask} = taskQuery;
  const {
    data: notificationQueryData,
    error: notificationError,
    isFetching: notificationFetching,
    refetch: refetchNotifications,
  } = notificationQuery;

  // Query's global focus refetch is deliberately disabled for every route
  // resource. Header historically did opt into focus manually, so retain that
  // narrow behavior here instead of changing Query defaults for the app.
  const lastTaskViewRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!shellScope) {
      lastTaskViewRef.current = null;
      return undefined;
    }
    if (lastTaskViewRef.current !== null && lastTaskViewRef.current !== view) {
      void refetchTask();
    }
    lastTaskViewRef.current = view;
    const refresh = () => {
      void refetchTask();
    };
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('focus', refresh);
      window.addEventListener(TASK_CHANGE_EVENT, refresh);
    }
    return () => {
      if (typeof window !== 'undefined' && window.removeEventListener) {
        window.removeEventListener('focus', refresh);
        window.removeEventListener(TASK_CHANGE_EVENT, refresh);
      }
    };
  }, [refetchTask, shellScope, view]);

  const lastNotificationViewRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!shellScope) {
      lastNotificationViewRef.current = null;
      return undefined;
    }
    if (lastNotificationViewRef.current !== null && lastNotificationViewRef.current !== view) {
      void refetchNotifications();
    }
    lastNotificationViewRef.current = view;
    const refresh = () => {
      void refetchNotifications();
    };
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('focus', refresh);
      window.addEventListener(TASK_CHANGE_EVENT, refresh);
      window.addEventListener(NOTIFICATIONS_CHANGE_EVENT, refresh);
    }
    return () => {
      if (typeof window !== 'undefined' && window.removeEventListener) {
        window.removeEventListener('focus', refresh);
        window.removeEventListener(TASK_CHANGE_EVENT, refresh);
        window.removeEventListener(NOTIFICATIONS_CHANGE_EVENT, refresh);
      }
    };
  }, [refetchNotifications, shellScope, view]);

  React.useEffect(() => {
    if (taskError && typeof console !== 'undefined' && console.warn) {
      console.warn('Header tasks badge: count failed', warnDetail(taskError));
    }
  }, [taskError]);

  React.useEffect(() => {
    if (notificationError && typeof console !== 'undefined' && console.warn) {
      console.warn('Header notifications: load failed', warnDetail(notificationError));
    }
  }, [notificationError]);

  // Preserve the old explicit failure values rather than exposing retained
  // Query data after a failed refetch. Keep them hidden while its retry is in
  // flight too: the old Header had already reset its local values to zero/[]
  // when the failure settled.
  const notificationFailed = Boolean(shellScope && notificationError && !notificationFetching);
  const notificationData = notificationError ? null : notificationQueryData;

  const retryNotifications = React.useCallback(() => {
    if (!shellScope) return Promise.resolve();
    return refetchNotifications();
  }, [refetchNotifications, shellScope]);

  return {
    taskBadgeCount: shellScope && !taskError ? taskData || 0 : 0,
    notificationUnread: shellScope && notificationData ? notificationData.unread || 0 : 0,
    notificationRecent: shellScope && notificationData ? notificationData.recent || [] : [],
    notificationLoading: Boolean(shellScope && notificationFetching),
    notificationFailed,
    retryNotifications,
  };
}
